import {
  BarChart3,
  BookOpenCheck,
  ChevronDown,
  FileClock,
  History,
  Inbox,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageCircleMore,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  Users,
  Database,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { initials } from "../lib/formatters";
import { navigate } from "../hooks/useHashRoute";
import { IconButton } from "../components/ui/Button";
import { BLUESUN_OFFICIAL_ICON } from "../lib/brand";
import ReleaseHistoryDialog from "../components/ReleaseHistoryDialog";
import { APP_VERSION, RELEASE_SEEN_STORAGE_KEY } from "../lib/releaseHistory";

const CORE_NAV = [
  { route: "dashboard", label: "今日工作台", icon: LayoutDashboard },
  { route: "inquiries", label: "询盘工作台", icon: Inbox },
  { route: "mailbox", label: "邮件中心", icon: FileClock },
  { route: "whatsapp", label: "WhatsApp 接待", icon: MessageCircleMore },
  { route: "joinf", label: "富通客户", icon: Database },
];

const SYSTEM_NAV = [
  { route: "system_logs", label: "系统日志", icon: BarChart3 },
  { route: "settings", label: "个人设置", icon: Settings },
];

export default function AppShell({ route, user, onLogout, onRouteIntent, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("bluesun:sidebar-collapsed") === "true",
  );
  const [accountOpen, setAccountOpen] = useState(false);
  const [releaseHistoryOpen, setReleaseHistoryOpen] = useState(() => {
    try {
      return localStorage.getItem(RELEASE_SEEN_STORAGE_KEY) !== "1";
    } catch {
      return true;
    }
  });
  const menuRef = useRef(null);
  const systemNav = [
    { route: "knowledge", label: "企业知识中心", icon: BookOpenCheck },
    ...SYSTEM_NAV,
    ...(user?.is_admin ? [{ route: "admin", label: "账号管理", icon: Users }] : []),
  ];

  useEffect(() => {
    setMobileOpen(false);
    setAccountOpen(false);
  }, [route]);

  useEffect(() => {
    const close = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setAccountOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      localStorage.setItem("bluesun:sidebar-collapsed", String(next));
      return next;
    });
  };

  const closeReleaseHistory = () => {
    try {
      localStorage.setItem(RELEASE_SEEN_STORAGE_KEY, "1");
    } catch {
      // The history remains usable when browser storage is unavailable.
    }
    setReleaseHistoryOpen(false);
  };

  const openReleaseHistory = () => {
    setAccountOpen(false);
    setReleaseHistoryOpen(true);
  };

  const NavGroup = ({ label, items }) => (
    <div className="nav-group">
      <span className="nav-group__label">{label}</span>
      {items.map(({ route: target, label: itemLabel, icon: Icon }) => (
        <button
          type="button"
          key={target}
          className={route === target ? "is-active" : ""}
          onPointerEnter={() => onRouteIntent?.(target)}
          onFocus={() => onRouteIntent?.(target)}
          onPointerDown={() => onRouteIntent?.(target)}
          onClick={() => navigate(target)}
          aria-current={route === target ? "page" : undefined}
          title={sidebarCollapsed ? itemLabel : undefined}
        >
          <Icon size={18} />
          <span>{itemLabel}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className={`app-layout ${sidebarCollapsed ? "is-sidebar-collapsed" : ""}`}>
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      {mobileOpen ? <button className="mobile-scrim" aria-label="关闭导航" onClick={() => setMobileOpen(false)} /> : null}
      <aside className={`app-sidebar ${mobileOpen ? "is-open" : ""} ${sidebarCollapsed ? "is-collapsed" : ""}`}>
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <img src={BLUESUN_OFFICIAL_ICON} alt="" loading="eager" decoding="async" />
          </span>
          <span><strong>BLUESUN</strong><small>外贸 AI 询盘系统</small></span>
          <IconButton className="mobile-nav-close" icon={X} label="关闭导航" onClick={() => setMobileOpen(false)} />
        </div>
        <nav aria-label="主导航">
          <NavGroup label="业务" items={CORE_NAV} />
          <NavGroup label="系统" items={systemNav} />
        </nav>
        <div className="sidebar-bottom">
          <div className="sidebar-footer">
            <span>BLUESUN 数智化产品团队</span>
            <button type="button" onClick={openReleaseHistory} aria-label={`查看 ${APP_VERSION} 版本迭代记录`}>
              <History size={13} />
              <small>Enterprise · {APP_VERSION}</small>
            </button>
          </div>
          <IconButton
            className="desktop-nav-toggle"
            icon={sidebarCollapsed ? PanelLeftOpen : PanelLeftClose}
            label={sidebarCollapsed ? "展开导航" : "折叠导航"}
            onClick={toggleSidebar}
          />
        </div>
      </aside>
      <section className="app-stage">
        <header className="app-header">
          <IconButton className="mobile-menu" icon={Menu} label="打开导航" onClick={() => setMobileOpen(true)} />
          <div className="app-header__context">
            <ShieldCheck size={17} />
            <span>企业数据已加密保护</span>
          </div>
          <div className="account-menu" ref={menuRef}>
            <button type="button" onClick={() => setAccountOpen((value) => !value)} aria-expanded={accountOpen}>
              <span className="avatar">{initials(user?.display_name || user?.username)}</span>
              <span className="account-copy">
                <strong>{user?.display_name || user?.username}</strong>
                <small>{user?.is_admin ? "系统管理员" : "外贸业务员"}</small>
              </span>
              <ChevronDown size={15} />
            </button>
            {accountOpen ? (
              <div className="account-popover">
                <button onClick={() => navigate("settings")}><Settings size={16} />个人设置</button>
                <button onClick={openReleaseHistory}><History size={16} />版本迭代记录</button>
                {user?.is_admin ? <button onClick={() => navigate("admin")}><Users size={16} />账号管理</button> : null}
                <hr />
                <button onClick={onLogout}><LogOut size={16} />退出登录</button>
              </div>
            ) : null}
          </div>
        </header>
        <main id="main-content" tabIndex="-1" className={`app-main app-main--${route}`}>{children}</main>
      </section>
      <ReleaseHistoryDialog open={releaseHistoryOpen} onClose={closeReleaseHistory} />
    </div>
  );
}
